import React, { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import editProfileUser from "../../../assets/images/others/host-side-images/editProfileUSer.png";
import { ReactComponent as EditProfilePersonal } from "../../../assets/images/svgs/edit-profiles.svg";
import { siteRoutes } from "../../../utils/helpers/enums/routes";
import { GENDERS, LANGUAGES } from "utils/helpers/enums/shared.enum";
import useAmbassador from "../useHooks";
import { EditProfileAmbassador } from "utils/helpers/models/ambassador/edit-profile.dto";
import useAuth from "containers/auth/useHooks";
import AmbassadorBreadcrumb, {
  AmbassadorBreadcrumbLink,
} from "components/breadcrumbs/AmbassadorBreadcrumb";
import PhoneNumberInput from "components/phone-number-input";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import { useSelector } from "react-redux";
import { errorToaster } from "utils/helpers/common/alert-service";

const AmbassadorEditProfile: React.FC = () => {
  const [formData, setFormData] = useState<EditProfileAmbassador>(
    new EditProfileAmbassador()
  );
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const { updateProfile, uploadFile } = useAmbassador();
  const { getStates } = useAuth();

  const breadcrumbLinks: AmbassadorBreadcrumbLink[] = [
    { title: "Home", path: siteRoutes.ambassadorHome },
    { title: "Edit Profile", path: siteRoutes.ambassadorEditProfile },
  ];

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
    control,
    watch,
    getValues,
  } = useForm<EditProfileAmbassador>({
    defaultValues: formData,
  });

  const maxLength = 60;

  const registeredData = useSelector(
    (state: any) => state.sharedReducer.registeredData
  );

  // Load states data and initialize formData on mount
  React.useEffect(() => {
    if (registeredData) {
      populateFormData();
    }
    getStates(setStates);
  }, [registeredData]);

  const populateFormData = () => {
    const combinedData = {
      ...registeredData?.user,
      ...registeredData?.profiles?.ambassador,
    };
    const initialData = {
      firstName:
        combinedData.firstName || registeredData?.user?.firstName || "",
      lastName: combinedData.lastName || registeredData?.user?.lastName || "",
      city: combinedData.city || registeredData?.user?.city || "",
      state: combinedData.state || registeredData?.user?.state || "",
      address: combinedData.address || registeredData?.user?.address || "",
      language: combinedData.language || registeredData?.user?.language || "",
      dob: combinedData.dob || registeredData?.user?.dob || "",
      gender: combinedData.gender || registeredData?.user?.gender || "",
      phone: combinedData.phone || registeredData?.user?.phone || "",
      zipCode: combinedData.zipCode || registeredData?.user?.zipCode || "",
      profilePicId:
        combinedData.profilePicUrl?.id ||
        registeredData?.user?.profilePicId ||
        0,
      email: combinedData.email || registeredData?.user?.email || "",
      mission: combinedData.mission || registeredData?.user?.mission || "",
      profilePicUrl:
        combinedData.profilePicUrl?.fileDownloadUri ||
        registeredData?.user?.profilePicUrl?.fileDownloadUri ||
        "",
    };
    setFormData(initialData as EditProfileAmbassador);
    Object.keys(initialData).forEach((key) => {
      const value = initialData[key as keyof typeof initialData];
      if (value !== null && value !== undefined) {
        setValue(key as keyof EditProfileAmbassador, value);
      }
    });
  };

  // Watch phone number field to sync with formData
  const phoneValue = watch("phone");

  // Sync phone number from react-hook-form to formData
  React.useEffect(() => {
    if (phoneValue !== formData.phone) {
      setFormData((prev) => ({ ...prev, phone: phoneValue || "" }));
    }
  }, [phoneValue]);

  // Load cities when state changes
  React.useEffect(() => {
    if (formData.state && states.length > 0) {
      const state = states.find((s) => s.name === formData.state);
      if (state) {
        setCities([...state.cities]);
      }
    }
  }, [formData.state, states]);

  const uploadProfilePic = (event: any) => {
    const { files } = event.target;
    const file = files[0];
    setValue("profilePicUrl", file);
    setFormData({ ...formData, profilePicUrl: URL.createObjectURL(file) });
  };

  const handleChange = (event: any) => {
    const { value, name } = event.target;
    formData[name] = value;
    setValue(name, formData[name], { shouldValidate: true });

    // Clear errors when user starts typing
    if (errors[name]) {
      clearErrors(name);
    }

    if (name === "state") {
      formData.city = "";
      setValue("city", "");
      clearErrors("city");
    }
    setFormData({ ...formData });
  };

  const onSubmit: SubmitHandler<EditProfileAmbassador> = async (data) => {
    // Get phone number from form and sync to formData (PhoneNumberInput updates form state, not formData)
    const phoneValue = getValues("phone");
    // Use validated data from form, but ensure phone is synced
    const submitData: EditProfileAmbassador = {
      ...formData,
      ...data,
      phone: phoneValue ? phoneValue.trim() : data.phone || formData.phone || "",
    };

    const profilePic = getValues("profilePicUrl");
    if (profilePic && typeof profilePic !== "string") {
      // New file uploaded, need to upload it first
      const imageData: any = await uploadFile(profilePic);
      if (imageData?.error) {
        // Upload failed - show error message
        errorToaster(imageData.error);
        return;
      } else if (imageData?.id) {
        submitData.profilePicId = imageData.id;
      } else {
        // Upload failed without specific error message
        errorToaster(
          "Failed to upload profile picture. Please try again with a supported image format (JPG, PNG, etc.)."
        );
        return;
      }
    } else if (!profilePic || typeof profilePic === "string") {
      // Use existing profile pic ID if no new file or if it's an existing URL
      if (registeredData?.user?.profilePicUrl?.id) {
        submitData.profilePicId = registeredData.user.profilePicUrl.id;
      } else if (submitData.profilePicId) {
        // Keep existing profilePicId
        // submitData.profilePicId is already set
      }
    }
    await updateProfile(submitData);
  };

  return (
    <div className="container min-h-[70vh] mb-6">
      <AmbassadorBreadcrumb links={breadcrumbLinks} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col items-center w-[270px] mb-8">
          <div className="relative">
            <img
              src={
                formData.profilePicUrl
                  ? typeof formData.profilePicUrl === "string" &&
                    formData.profilePicUrl.startsWith("blob:")
                    ? formData.profilePicUrl
                    : concatPathWithBackendUrl(formData.profilePicUrl)
                  : editProfileUser
              }
              alt="Profile"
              className="w-36 h-36 rounded-full object-cover"
            />
            {formData.profilePicUrl ? (
              <>
                <label
                  htmlFor="profilePicUrl"
                  className="absolute bottom-2 right-0 bg-white shadow p-2 rounded-full cursor-pointer"
                >
                  <EditProfilePersonal />
                </label>
              </>
            ) : (
              <label
                htmlFor="profilePicUrl"
                className="absolute bottom-2 right-0 bg-white shadow p-2 rounded-full cursor-pointer"
              >
                <EditProfilePersonal />
              </label>
            )}
            <input
              id="profilePicUrl"
              type="file"
              accept="image/*"
              className="hidden"
              {...register("profilePicUrl", { required: false })}
              onChange={uploadProfilePic}
            />
          </div>
          {errors.profilePicUrl && (
            <p className="text-red-500 text-sm mt-1">
              {errors.profilePicUrl.message}
            </p>
          )}
          <div className="flex flex-col gap-2 items-center mt-3">
            <p className="text-base text-label px-5 font-semibold">
              Upload Profile Picture
            </p>
            <p className="text-labelTextColor text-center text-sm">
              For security purposes, only use updated/recent picture of
              ambassador by which host can identify you
            </p>
          </div>
        </div>

        <div className="w-full my-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="flex flex-col gap-1">
            <label className="custom-label">First name</label>
            <input
              type="text"
              className="custom-input"
              {...register("firstName", { required: "First name is required" })}
              value={formData.firstName}
              onChange={handleChange}
            />
            {errors.firstName && (
              <p className="text-red-500 text-sm">{errors.firstName.message}</p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <label className="custom-label">Last name</label>
            <input
              type="text"
              className="custom-input"
              {...register("lastName", { required: "Last name is required" })}
              value={formData.lastName}
              onChange={handleChange}
            />
            {errors.lastName && (
              <p className="text-red-500 text-sm">{errors.lastName.message}</p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <label className="custom-label">Email</label>
            <input
              type="email"
              className="custom-input"
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^\S+@\S+$/i,
                  message: "Enter a valid email",
                },
              })}
              value={formData.email}
              onChange={handleChange}
            />
            {errors.email && (
              <p className="text-red-500 text-sm">{errors.email.message}</p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <label className="custom-label">Language</label>
            <select
              className="custom-input"
              {...register("language", { required: "Language is required" })}
              value={formData.language}
              onChange={handleChange}
            >
              <option value="" disabled>
                Select Language
              </option>
              {Object.values(LANGUAGES).map((lang, index) => {
                return (
                  <option value={lang} key={index}>
                    {lang}
                  </option>
                );
              })}
            </select>
            {errors.language && (
              <p className="text-red-500 text-sm">{errors.language.message}</p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <label className="custom-label">Date of Birth</label>
            <input
              type="date"
              placeholder="Select Date"
              className="custom-input"
              {...register("dob", { required: "Date of Birth is required" })}
              value={formData.dob}
              onChange={handleChange}
            />
            {errors.dob && (
              <p className="text-red-500 text-sm">{errors.dob.message}</p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <label className="custom-label">Gender</label>
            <select
              className="custom-input"
              {...register("gender", { required: "Gender is required" })}
              value={formData.gender}
              onChange={handleChange}
            >
              <option value="">Select Gender</option>
              {Object.values(GENDERS).map((gender, index) => {
                return (
                  <option value={gender} key={index}>
                    {gender}
                  </option>
                );
              })}
            </select>
            {errors.gender && (
              <p className="text-red-500 text-sm">{errors.gender.message}</p>
            )}
          </div>

          {/* Phone Number */}
          <div className="flex flex-col">
            <PhoneNumberInput
              control={control}
              name="phone"
              label="Phone Number"
              error={errors.phone}
              placeholder="(123) 456-7890"
              required={true}
              className="custom-input"
              labelClassName="custom-label"
              gap="gap-1"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="custom-label">Address</label>
            <input
              type="text"
              className="custom-input"
              {...register("address", { required: "Address is required" })}
              value={formData.address}
              onChange={handleChange}
            />
            {errors.address && (
              <p className="text-red-500 text-sm">{errors.address.message}</p>
            )}
          </div>

          {/* State Dropdown */}
          <div className="flex flex-col gap-1">
            <label className="custom-label">State</label>
            <select
              className="custom-input"
              {...register("state", {
                required: "State is required",
              })}
              value={formData.state}
              onChange={handleChange}
            >
              <option value="" disabled>
                Select
              </option>
              {states.map((state, index) => (
                <option key={index} value={state.name}>
                  {state.name}
                </option>
              ))}
            </select>
            {errors.state && (
              <p className="text-red-500 text-sm">{errors.state.message}</p>
            )}
          </div>

          {/* City Dropdown */}
          <div className="flex flex-col gap-1">
            <label className="custom-label">City</label>
            <select
              className="custom-input"
              {...register("city", {
                required: false,
              })}
              value={formData.city}
              onChange={handleChange}
            >
              <option value="" disabled>
                Select
              </option>
              {cities.map((city, index) => (
                <option key={index} value={city.name}>
                  {city.name}
                </option>
              ))}
            </select>
            {errors.city && (
              <p className="text-red-500 text-sm">{errors.city.message}</p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <label className="custom-label">Zip Code</label>
            <input
              type="text"
              className="custom-input"
              {...register("zipCode", { required: "Zip Code is required" })}
              value={formData.zipCode}
              onChange={handleChange}
            />
            {errors.zipCode && (
              <p className="text-red-500 text-sm">{errors.zipCode.message}</p>
            )}
          </div>

          <div className="flex flex-col gap-1 relative">
            <div className="flex items-center justify-between">
              <label className="custom-label">Mission</label>
              <span className="text-primaryBlueText text-xs">
                {formData.mission ? formData.mission.length : 0}/{maxLength}
              </span>
            </div>
            <input
              type="text"
              className="custom-input"
              {...register("mission", {
                required: "mission is required",
              })}
              value={formData.mission}
              onChange={handleChange}
              maxLength={maxLength}
            />
            {errors.mission && (
              <p className="text-red-500 text-sm">{errors.mission.message}</p>
            )}
          </div>
        </div>

        <button
          type="submit"
          className="px-4 py-2 bg-primaryBlueBg text-white rounded-full mt-4 md:mt-8"
        >
          Update
        </button>
      </form>
    </div>
  );
};

export default AmbassadorEditProfile;
